import { useEffect, useRef, useState } from 'react';
import { supabase } from './lib/supabase.js';

// Crockford-ish alphabet without ambiguous chars (no 0, O, 1, I).
const RECOVERY_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

// Race a promise against a timeout. Returns { kind: 'value', value } or
// { kind: 'timeout' }. Used so a stuck Supabase auth call can't trap us
// forever on the "Prüfe…" screen — we get a chance to look at the
// authoritative AAL state instead.
async function raceWithTimeout(promise, ms) {
  let to;
  const timeout = new Promise((resolve) => {
    to = setTimeout(() => resolve({ kind: 'timeout' }), ms);
  });
  const value = promise.then((v) => ({ kind: 'value', value: v }));
  const result = await Promise.race([value, timeout]);
  clearTimeout(to);
  return result;
}

// Truth check that side-steps verify()'s return value. The verify call can
// return a "Lock was released because another request stole it" error even
// when the session was actually upgraded — and onAuthStateChange can fail
// to fire. The only authoritative source is the AAL of the current JWT.
async function isSessionAtAal2() {
  const r = await raceWithTimeout(
    supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
    5000,
  );
  if (r.kind !== 'value') return false;
  return r.value?.data?.currentLevel === 'aal2';
}

function generateRecoveryCodes(count = 10) {
  const codes = [];
  for (let i = 0; i < count; i++) {
    const buf = new Uint8Array(8);
    crypto.getRandomValues(buf);
    let s = '';
    for (let j = 0; j < 8; j++) s += RECOVERY_ALPHABET[buf[j] % RECOVERY_ALPHABET.length];
    codes.push(s.slice(0, 4) + '-' + s.slice(4));
  }
  return codes;
}

// Full-page wrapper that mirrors Login.jsx for the gate screens.
function GateShell({ title, eyebrow, children }) {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      background: '#0B0D13',
      color: 'var(--ink)',
      fontFamily: 'var(--sans)',
      padding: 24,
    }}>
      <div style={{
        width: 460,
        maxWidth: '100%',
        background: 'var(--bg-elev)',
        border: '1px solid var(--stroke-2)',
        borderRadius: 'var(--radius-xl)',
        padding: 36,
        boxShadow: 'var(--shadow-lg)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img
            src="/assets/markenegger-wordmark.png"
            alt="Markenegger"
            style={{ height: 28, opacity: 0.95, filter: 'invert(1) hue-rotate(180deg) brightness(1.05)' }}
          />
          <div className="eyebrow" style={{ marginTop: 14 }}>{eyebrow}</div>
          <h1 className="serif" style={{ fontSize: 22, fontWeight: 500, margin: '8px 0 0', letterSpacing: '-0.01em' }}>
            {title}
          </h1>
        </div>
        {children}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// MfaChallenge — shown after magic-link login when the user has a verified
// TOTP factor. Steps the session up from AAL1 to AAL2.
// -----------------------------------------------------------------------------
function MfaChallenge({ onSignOut, onComplete }) {
  const [code, setCode] = useState('');
  const [factorId, setFactorId] = useState(null);
  const [challengeId, setChallengeId] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | verifying | error
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const lfRes = await raceWithTimeout(supabase.auth.mfa.listFactors(), 8000);
      if (cancelled) return;
      if (lfRes.kind === 'timeout') {
        setError('Zeitüberschreitung beim Laden der 2FA-Faktoren. Bitte Seite neu laden.');
        setStatus('error');
        return;
      }
      const { data, error: lfErr } = lfRes.value;
      if (lfErr || !data?.totp?.length) {
        setError(lfErr?.message || 'Kein TOTP-Faktor gefunden');
        setStatus('error');
        return;
      }
      const verified = data.totp.find(f => f.status === 'verified') || data.totp[0];
      const chRes = await raceWithTimeout(
        supabase.auth.mfa.challenge({ factorId: verified.id }),
        8000,
      );
      if (cancelled) return;
      if (chRes.kind === 'timeout') {
        setError('Zeitüberschreitung. Bitte Seite neu laden.');
        setStatus('error');
        return;
      }
      const { data: chData, error: chErr } = chRes.value;
      if (chErr) {
        setError(chErr.message);
        setStatus('error');
        return;
      }
      setFactorId(verified.id);
      setChallengeId(chData.id);
      setStatus('ready');
    })();
    return () => { cancelled = true; };
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (code.length !== 6 || status === 'verifying') return;
    setStatus('verifying');
    setError(null);

    // Step 1: fire verify with a hard timeout so a stuck network call
    // can't park us on "Prüfe…" indefinitely.
    const vResult = await raceWithTimeout(
      supabase.auth.mfa.verify({ factorId, challengeId, code }),
      12000,
    ).catch((err) => ({ kind: 'value', value: { error: err } }));

    // Step 2: check the actual session state. This is the only authoritative
    // signal — verify()'s return value can be misleading (lock-stolen
    // "errors" that aren't actually errors, retries that succeeded under
    // the hood, etc.).
    if (await isSessionAtAal2()) {
      onComplete?.();
      return;
    }

    // Step 3: real failure. Surface a useful message and let the user retry.
    let message = 'Code falsch oder abgelaufen. Bitte aktuellen Code aus der App eingeben.';
    if (vResult.kind === 'timeout') {
      message = 'Zeitüberschreitung. Bitte erneut versuchen.';
    } else if (vResult.value?.error?.message) {
      const m = vResult.value.error.message;
      // Suppress the noisy lock-stolen warning — at this point we already
      // know AAL didn't upgrade, so a retry is what's needed regardless.
      if (!/lock.*was released|stole it/i.test(m)) {
        message = m;
      }
    }
    setError(message);
    setStatus('ready');
    setCode('');

    // Refresh the challenge for the next attempt. Same defensive pattern.
    const cResult = await raceWithTimeout(
      supabase.auth.mfa.challenge({ factorId }),
      5000,
    ).catch(() => ({ kind: 'timeout' }));
    if (cResult.kind === 'value' && cResult.value?.data?.id) {
      setChallengeId(cResult.value.data.id);
    }
  }

  return (
    <GateShell eyebrow="Zwei-Faktor-Authentifizierung" title="Code aus der Authenticator-App">
      <form onSubmit={submit}>
        <div className="field">
          <label>6-stelliger Code</label>
          <input
            className="input mono"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="••••••"
            autoFocus
            disabled={status !== 'ready'}
            style={{ fontSize: 22, letterSpacing: '0.3em', textAlign: 'center' }}
          />
        </div>
        {error && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 10 }}>{error}</div>}
        <button
          type="submit"
          className="btn btn-primary"
          style={{ width: '100%', justifyContent: 'center', marginTop: 16, opacity: (code.length === 6 && status === 'ready') ? 1 : 0.6 }}
          disabled={code.length !== 6 || status !== 'ready'}
        >
          {status === 'verifying' ? 'Prüfe…' : 'Bestätigen'}
        </button>
        <div className="tiny muted" style={{ marginTop: 16, textAlign: 'center', lineHeight: 1.5 }}>
          Gerät verloren? <button type="button" onClick={onSignOut}
            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' }}>
            Abmelden und Wiederherstellungs-Code verwenden
          </button>
        </div>
      </form>
    </GateShell>
  );
}

// -----------------------------------------------------------------------------
// MfaEnrollment — required first-run screen for any user without a verified
// factor. Shows QR, verifies the first code, then displays recovery codes.
// -----------------------------------------------------------------------------
function MfaEnrollment({ onSignOut, onLock, onComplete }) {
  const [stage, setStage] = useState('init'); // init | scan | verifying | recovery | done
  const [factor, setFactor] = useState(null);
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      // Clean up any leftover unverified factors so enroll() doesn't hit
      // "factor already exists".
      const { data: lf } = await supabase.auth.mfa.listFactors();
      const stale = (lf?.all || []).filter(f => f.status !== 'verified');
      for (const f of stale) {
        await supabase.auth.mfa.unenroll({ factorId: f.id });
      }
      const { data, error: enErr } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
      if (enErr) {
        setError(enErr.message);
        return;
      }
      setFactor(data);
      setStage('scan');
    })();
  }, []);

  async function verifyCode(e) {
    e.preventDefault();
    if (code.length !== 6 || !factor) return;
    setStage('verifying');
    setError(null);
    // Lock Root's gate re-evaluation BEFORE verify() so the impending AAL
    // upgrade doesn't unmount us mid-flow.
    onLock?.();

    const chRes = await raceWithTimeout(
      supabase.auth.mfa.challenge({ factorId: factor.id }),
      8000,
    ).catch((err) => ({ kind: 'value', value: { error: err } }));

    if (chRes.kind === 'timeout' || chRes.value?.error) {
      setError(chRes.kind === 'timeout' ? 'Zeitüberschreitung — bitte erneut.' : chRes.value.error.message);
      setStage('scan'); setCode(''); return;
    }
    const chData = chRes.value.data;
    setChallengeId(chData.id);

    const vRes = await raceWithTimeout(
      supabase.auth.mfa.verify({
        factorId: factor.id,
        challengeId: chData.id,
        code,
      }),
      12000,
    ).catch((err) => ({ kind: 'value', value: { error: err } }));

    // Authoritative check: did the session actually upgrade?
    if (!(await isSessionAtAal2())) {
      let message = 'Code falsch oder abgelaufen. Bitte aktuellen Code aus der App eingeben.';
      if (vRes.kind === 'timeout') {
        message = 'Zeitüberschreitung. Bitte erneut versuchen.';
      } else if (vRes.value?.error?.message) {
        const m = vRes.value.error.message;
        if (!/lock.*was released|stole it/i.test(m)) message = m;
      }
      setError(message);
      setStage('scan');
      setCode('');
      return;
    }
    // Generate, store, and display recovery codes.
    const codes = generateRecoveryCodes(10);
    const { error: rcErr } = await supabase.rpc('replace_recovery_codes', { p_codes: codes });
    if (rcErr) {
      setError('TOTP aktiviert, aber Recovery-Codes konnten nicht gespeichert werden: ' + rcErr.message);
      // Still show codes so user has something — they can regenerate later.
    }
    setRecoveryCodes(codes);
    setStage('recovery');
  }

  function copyAll() {
    navigator.clipboard?.writeText(recoveryCodes.join('\n'));
  }

  function downloadCodes() {
    const text =
      'Markenegger PM — Wiederherstellungs-Codes\n' +
      'Erzeugt: ' + new Date().toLocaleString('de-DE') + '\n\n' +
      recoveryCodes.join('\n') + '\n\n' +
      'Jeder Code ist nur einmal verwendbar. Bewahre sie sicher auf.\n';
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'markenegger-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function finish() {
    setStage('done');
    onComplete?.();
  }

  if (stage === 'init' || !factor) {
    return (
      <GateShell eyebrow="Sicherheit einrichten" title="Zwei-Faktor wird vorbereitet…">
        {error
          ? <div className="tiny" style={{ color: 'var(--crit)' }}>{error}</div>
          : <div className="tiny muted" style={{ textAlign: 'center' }}>Einen Moment.</div>}
      </GateShell>
    );
  }

  if (stage === 'scan' || stage === 'verifying') {
    return (
      <GateShell eyebrow="Schritt 1 von 2" title="Authenticator-App einrichten">
        <div className="tiny muted" style={{ marginBottom: 14, lineHeight: 1.5 }}>
          Scanne den QR-Code mit Google Authenticator (oder einer anderen TOTP-App)
          und gib den 6-stelligen Code ein.
        </div>
        <div style={{
          display: 'grid', placeItems: 'center',
          background: '#fff', borderRadius: 12, padding: 16, marginBottom: 14,
        }}>
          <img src={factor.totp.qr_code} alt="TOTP QR Code" style={{ width: 200, height: 200 }}/>
        </div>
        <details style={{ marginBottom: 14 }}>
          <summary className="tiny muted" style={{ cursor: 'pointer' }}>Code manuell eingeben</summary>
          <div className="mono tiny" style={{
            marginTop: 8, padding: 10, background: 'var(--bg)', borderRadius: 8,
            border: '1px solid var(--stroke)', wordBreak: 'break-all',
          }}>
            {factor.totp.secret}
          </div>
        </details>
        <form onSubmit={verifyCode}>
          <div className="field">
            <label>Code aus der App</label>
            <input
              className="input mono"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••"
              autoFocus
              disabled={stage === 'verifying'}
              style={{ fontSize: 22, letterSpacing: '0.3em', textAlign: 'center' }}
            />
          </div>
          {error && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 10 }}>{error}</div>}
          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', marginTop: 16, opacity: (code.length === 6 && stage !== 'verifying') ? 1 : 0.6 }}
            disabled={code.length !== 6 || stage === 'verifying'}
          >
            {stage === 'verifying' ? 'Prüfe…' : 'Bestätigen & weiter'}
          </button>
        </form>
        <div className="tiny muted" style={{ marginTop: 16, textAlign: 'center' }}>
          <button type="button" onClick={onSignOut}
            style={{ background: 'none', border: 'none', color: 'var(--ink-3)', cursor: 'pointer', textDecoration: 'underline' }}>
            Abmelden
          </button>
        </div>
      </GateShell>
    );
  }

  if (stage === 'recovery') {
    return (
      <GateShell eyebrow="Schritt 2 von 2" title="Wiederherstellungs-Codes">
        <div className="tiny muted" style={{ marginBottom: 14, lineHeight: 1.5 }}>
          Speichere diese Codes <b style={{ color: 'var(--ink-2)' }}>jetzt</b> an einem
          sicheren Ort (Passwort-Manager). Falls du dein Gerät verlierst, kannst du dich
          mit einem dieser Codes wieder anmelden. Jeder Code ist nur einmal gültig — und
          sie werden danach nie wieder angezeigt.
        </div>
        <div className="mono" style={{
          background: 'var(--bg)', border: '1px solid var(--stroke)',
          borderRadius: 12, padding: 16,
          fontSize: 14, lineHeight: 1.9,
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px',
        }}>
          {recoveryCodes.map(c => <div key={c}>{c}</div>)}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={copyAll}>
            Kopieren
          </button>
          <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={downloadCodes}>
            Als .txt herunterladen
          </button>
        </div>
        {error && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 12 }}>{error}</div>}
        <label className="tiny" style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          marginTop: 18, padding: 12, borderRadius: 8,
          background: 'var(--bg)', border: '1px solid var(--stroke)',
          cursor: 'pointer', lineHeight: 1.5,
        }}>
          <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} style={{ marginTop: 2 }}/>
          <span>Ich habe die Codes sicher gespeichert. Ohne sie verliere ich bei Geräteverlust den Zugang.</span>
        </label>
        <button
          type="button"
          className="btn btn-primary"
          style={{ width: '100%', justifyContent: 'center', marginTop: 14, opacity: confirmed ? 1 : 0.6 }}
          disabled={!confirmed}
          onClick={finish}
        >
          Fertig — zur App
        </button>
      </GateShell>
    );
  }

  return (
    <GateShell eyebrow="Sicherheit eingerichtet" title="Zwei-Faktor ist aktiv.">
      <div className="tiny muted" style={{ textAlign: 'center' }}>Lade…</div>
    </GateShell>
  );
}

// -----------------------------------------------------------------------------
// RecoveryLogin — alternative login path for users who lost their device.
// Verifies a recovery code (which deletes their TOTP factor) and then sends
// a fresh magic link. After login, MfaEnrollment kicks in to enroll a new
// device.
// -----------------------------------------------------------------------------
function RecoveryLogin({ onCancel }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('idle'); // idle | working | sent | error
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (!email || !code || status === 'working') return;
    setStatus('working');
    setError(null);
    const { data, error: rpcErr } = await supabase.rpc('use_recovery_code', {
      p_email: email,
      p_code: code,
    });
    if (rpcErr) {
      setError(rpcErr.message); setStatus('error'); return;
    }
    if (!data) {
      setError('Code ungültig oder bereits verbraucht.'); setStatus('error'); return;
    }
    const { error: otpErr } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (otpErr) {
      setError(otpErr.message); setStatus('error'); return;
    }
    setStatus('sent');
  }

  return (
    <GateShell eyebrow="Wiederherstellung" title="Mit Recovery-Code anmelden">
      {status === 'sent' ? (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%',
            background: 'var(--ok-soft)', color: 'var(--ok)',
            display: 'grid', placeItems: 'center',
            margin: '0 auto 14px', fontSize: 28,
          }}>✓</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Code akzeptiert.</div>
          <div className="tiny muted" style={{ marginTop: 8, lineHeight: 1.5 }}>
            Wir haben den 2FA-Faktor deines Accounts zurückgesetzt und einen neuen Login-Link
            an <b style={{ color: 'var(--ink-2)' }}>{email}</b> geschickt. Nach dem Klick richtest
            du ein neues Gerät ein.
          </div>
          <button className="btn btn-ghost" style={{ marginTop: 20 }} onClick={onCancel}>Zurück zum Login</button>
        </div>
      ) : (
        <form onSubmit={submit}>
          <div className="tiny muted" style={{ marginBottom: 14, lineHeight: 1.5 }}>
            Gib deine E-Mail und einen deiner Wiederherstellungs-Codes ein. Wir setzen
            den Authenticator zurück und schicken dir einen neuen Login-Link.
          </div>
          <div className="field">
            <label>E-Mail-Adresse</label>
            <input
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="du@markenegger.com"
              required
              autoFocus
              disabled={status === 'working'}
            />
          </div>
          <div className="field" style={{ marginTop: 12 }}>
            <label>Wiederherstellungs-Code</label>
            <input
              className="input mono"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX"
              required
              disabled={status === 'working'}
              style={{ letterSpacing: '0.15em', textAlign: 'center' }}
            />
          </div>
          {error && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 10 }}>{error}</div>}
          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', marginTop: 16, opacity: status === 'working' ? 0.6 : 1 }}
            disabled={status === 'working'}
          >
            {status === 'working' ? 'Prüfe…' : 'Code einlösen'}
          </button>
          <div className="tiny muted" style={{ marginTop: 16, textAlign: 'center' }}>
            <button type="button" onClick={onCancel}
              style={{ background: 'none', border: 'none', color: 'var(--ink-3)', cursor: 'pointer', textDecoration: 'underline' }}>
              Zurück zum normalen Login
            </button>
          </div>
        </form>
      )}
    </GateShell>
  );
}

// -----------------------------------------------------------------------------
// MfaSettingsCard — shown on the Settings page. Confirms 2FA is active and
// lets the user regenerate their recovery codes (which invalidates the old
// set).
// -----------------------------------------------------------------------------
function MfaSettingsCard() {
  const [unusedCount, setUnusedCount] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [stage, setStage] = useState('idle'); // idle | confirming | generating | shown | error
  const [newCodes, setNewCodes] = useState([]);
  const [error, setError] = useState(null);

  async function loadCounts() {
    const { data, error: e } = await supabase
      .from('mfa_recovery_codes')
      .select('used_at');
    if (e) return;
    setUnusedCount((data || []).filter(c => !c.used_at).length);
    setTotalCount((data || []).length);
  }

  useEffect(() => { loadCounts(); }, []);

  async function regenerate() {
    setStage('generating');
    setError(null);
    const codes = generateRecoveryCodes(10);
    const { error: e } = await supabase.rpc('replace_recovery_codes', { p_codes: codes });
    if (e) {
      setError(e.message); setStage('error'); return;
    }
    setNewCodes(codes);
    setStage('shown');
    loadCounts();
  }

  function copy() { navigator.clipboard?.writeText(newCodes.join('\n')); }
  function download() {
    const text =
      'Markenegger PM — Wiederherstellungs-Codes\n' +
      'Erzeugt: ' + new Date().toLocaleString('de-DE') + '\n\n' +
      newCodes.join('\n') + '\n';
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'markenegger-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="card card-pad-lg" style={{ marginBottom: 20 }}>
      <div className="card-title"><span>Zwei-Faktor-Authentifizierung</span></div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'var(--ok-soft)', color: 'var(--ok)',
          display: 'grid', placeItems: 'center', fontSize: 18,
        }}>✓</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Aktiv</div>
          <div className="tiny muted">Authenticator-App ist verknüpft.</div>
        </div>
      </div>

      <div className="tiny muted" style={{ lineHeight: 1.5, marginBottom: 14 }}>
        Wiederherstellungs-Codes:{' '}
        <b style={{ color: 'var(--ink-2)' }}>
          {unusedCount === null ? '…' : `${unusedCount} von ${totalCount} unbenutzt`}
        </b>
        . Diese Codes brauchst du, falls du dein Authenticator-Gerät verlierst.
      </div>

      {stage !== 'shown' && (
        <>
          {stage !== 'confirming' ? (
            <button className="btn" onClick={() => setStage('confirming')}>
              Neue Wiederherstellungs-Codes erzeugen
            </button>
          ) : (
            <div style={{
              padding: 14, borderRadius: 10,
              background: 'var(--bg)', border: '1px solid var(--stroke)',
            }}>
              <div className="tiny" style={{ lineHeight: 1.5, marginBottom: 12 }}>
                Wenn du neue Codes erzeugst, werden <b>alle alten ungültig</b>.
                Stelle sicher, dass du die neuen Codes danach sicher speicherst.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" onClick={regenerate} disabled={stage === 'generating'}>
                  {stage === 'generating' ? 'Erzeuge…' : 'Ja, neue Codes erzeugen'}
                </button>
                <button className="btn btn-ghost" onClick={() => setStage('idle')}>Abbrechen</button>
              </div>
            </div>
          )}
          {error && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 10 }}>{error}</div>}
        </>
      )}

      {stage === 'shown' && (
        <div>
          <div className="tiny" style={{ lineHeight: 1.5, marginBottom: 10, color: 'var(--ink-2)' }}>
            Hier sind deine neuen Codes — sie werden danach <b>nie wieder angezeigt</b>.
          </div>
          <div className="mono" style={{
            background: 'var(--bg)', border: '1px solid var(--stroke)',
            borderRadius: 12, padding: 16,
            fontSize: 14, lineHeight: 1.9,
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px',
          }}>
            {newCodes.map(c => <div key={c}>{c}</div>)}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={copy}>Kopieren</button>
            <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={download}>Als .txt herunterladen</button>
          </div>
          <button className="btn btn-ghost" style={{ marginTop: 10 }}
            onClick={() => { setNewCodes([]); setStage('idle'); }}>
            Ich habe die Codes gespeichert
          </button>
        </div>
      )}
    </div>
  );
}

export { MfaEnrollment, MfaChallenge, RecoveryLogin, MfaSettingsCard, generateRecoveryCodes };
